import { AccountTypeEnum, ComplianceObjectType } from "../graphql/supergraphEnums";
import { type AccountTypeEnum as AccountType } from "../graphql/supergraphTypes";

type AccountTypeOption = {
  key: AccountTypeEnum;
  label: string;
};

const individualAccountTypes = new Set<AccountType>([
  AccountTypeEnum.Individual,
  AccountTypeEnum.IraOrNonUs,
  AccountTypeEnum.SelfDirectedIra,
]);

const joinAccountTypes = new Set<AccountType>([
  AccountTypeEnum.Joint,
  AccountTypeEnum.Tic,
  AccountTypeEnum.JointOther,
]);

export const commonEntityAccountTypes = new Set<AccountType>([
  AccountTypeEnum.RevocableTrust,
  AccountTypeEnum.IrrevocableTrust,
  AccountTypeEnum.Corporation,
  AccountTypeEnum.Llc,
  AccountTypeEnum.Partnership,
  AccountTypeEnum.Field_501c3,
  AccountTypeEnum.NonProfit,
  AccountTypeEnum.Foundation,
  AccountTypeEnum.EmployeePension,
  AccountTypeEnum.PensionPlan,
  AccountTypeEnum.Custodian,
]);

export const extendedEntityAccountTypes = new Set<AccountType>([
  AccountTypeEnum.Dac,
  AccountTypeEnum.Sarl,
  AccountTypeEnum.Sa,
  AccountTypeEnum.Ptc,
  AccountTypeEnum.Spc,
  AccountTypeEnum.Llp,
  AccountTypeEnum.Ltd,
  AccountTypeEnum.Gmbh,
  AccountTypeEnum.Ag,
  AccountTypeEnum.Plc,
  AccountTypeEnum.Lllp,
  AccountTypeEnum.Sovereign,
]);

export const resolvedAccountType = (
  profile:
    | {
        accountType?: AccountType | null;
        complianceObjectType?: ComplianceObjectType | string | null;
      }
    | null
    | undefined,
): AccountType | undefined => {
  if (profile?.accountType) return profile?.accountType as AccountTypeEnum;
  if (profile?.complianceObjectType === ComplianceObjectType.Individual) {
    return AccountTypeEnum.Individual;
  }
  return undefined;
};

export const isIndividualAccountType = (
  entityType: AccountType | null | undefined,
): boolean => {
  if (!entityType) return false;
  return individualAccountTypes.has(entityType);
};

/**
 * Determines if the given entity type requires domicile address information
 * @param entityType - The entity type to check
 * @returns true if domicile address is required, false otherwise
 */
export const isJoinAccountType = (accountType: AccountType | null | undefined): boolean => {
  if (!accountType) return false;
  return joinAccountTypes.has(accountType as AccountTypeEnum);
};

/**
 * Get account type category for warning logic
 * TODO: Return ComplianceObjectType enum here as "individual", "Joint", "Institution"
 * once backend change is merged
 */
export const getAccountTypeCategory = (accountType: AccountType | null | undefined) => {
  if (!accountType) return null;
  if (isIndividualAccountType(accountType)) return "individual";
  if (isJoinAccountType(accountType)) return "joint";
  return "entity";
};

/**
 * Label mappings for individual account types
 */
export const individualTypeOptions: readonly AccountTypeOption[] = [
  { key: AccountTypeEnum.Individual, label: "Individual" },
  { key: AccountTypeEnum.IraOrNonUs, label: "IRA or Non-US Retirement Account" },
  { key: AccountTypeEnum.SelfDirectedIra, label: "Self-Directed Retirement Account" },
];

/**
 * Label mappings for entity account types
 */
export const jointAccountTypeLabels: readonly AccountTypeOption[] = [
  { key: AccountTypeEnum.Joint, label: "Joint Tenant with Rights of Survivorship" },
  { key: AccountTypeEnum.JointOther, label: "Other Joint Ownership" },
  { key: AccountTypeEnum.Tic, label: "Joint Tenants in Common" },
];

/**
 * Label mappings for entity account types
 */
const commonEntityAccountTypeLabels: readonly AccountTypeOption[] = [
  { key: AccountTypeEnum.RevocableTrust, label: "Revocable Trust" },
  { key: AccountTypeEnum.IrrevocableTrust, label: "Irrevocable Trust" },
  { key: AccountTypeEnum.Corporation, label: "Corporation" },
  { key: AccountTypeEnum.Llc, label: "Limited Liability Company (LLC)" },
  { key: AccountTypeEnum.Partnership, label: "Partnership" },
  { key: AccountTypeEnum.Field_501c3, label: "501(c)(3)" },
  { key: AccountTypeEnum.NonProfit, label: "Non-Profit" },
  { key: AccountTypeEnum.Foundation, label: "Foundation" },
  {
    key: AccountTypeEnum.EmployeePension,
    label: "Employee Benefit Plan, Trust, or Pension Plan",
  },
  { key: AccountTypeEnum.PensionPlan, label: "State or Municipal Government Pension Plan" },
  { key: AccountTypeEnum.Custodian, label: "Custodian, Nominee or Agent" },
];

const extendedEntityAccountTypeLabels: readonly AccountTypeOption[] = [
  { key: AccountTypeEnum.Dac, label: "Designated Activity Company (DAC)" },
  { key: AccountTypeEnum.Sarl, label: "Société à Responsabilité Limitée (SARL)" },
  { key: AccountTypeEnum.Sa, label: "Société Anonyme (SA)" },
  { key: AccountTypeEnum.Ptc, label: "BVI Private Trust Company (PTC)" },
  { key: AccountTypeEnum.Spc, label: "Segregated Portfolio Company (SPC)" },
  { key: AccountTypeEnum.Llp, label: "Limited Liability Partnership (LLP)" },
  { key: AccountTypeEnum.Ltd, label: "Private Limited Company (Ltd)" },
  { key: AccountTypeEnum.Gmbh, label: "Gesellschaft mit beschränkter Haftung (GmbH)" },
  { key: AccountTypeEnum.Ag, label: "Aktiengesellschaft (AG)" },
  { key: AccountTypeEnum.Plc, label: "Public Limited Company (PLC)" },
  { key: AccountTypeEnum.Lllp, label: "Limited Liability Limited Partnership (LLLP)" },
  { key: AccountTypeEnum.Sovereign, label: "Sovereign Entity" },
];

const allAccountTypeLabels = new Map<string, string>(
  [
    ...individualTypeOptions,
    ...jointAccountTypeLabels,
    ...commonEntityAccountTypeLabels,
    ...extendedEntityAccountTypeLabels,
  ].map(({ key, label }) => [key, label]),
);

/**
 * Get the display label for a given account type.
 */
export const getAccountTypeLabel = (
  accountType: AccountType | null | undefined,
): string | undefined => {
  if (!accountType) return "--";
  return allAccountTypeLabels.get(accountType);
};

/**
 * Get all entity account types with their labels as {value, label} pairs
 */
export const getAllEntityAccountTypes = () => {
  const accountTypes = [...commonEntityAccountTypeLabels, ...extendedEntityAccountTypeLabels];

  return accountTypes.map((option) => ({
    value: option.key,
    label: option.label,
  }));
};

/**
 * Get all supported account types (individual + join + entity) with their labels as {value, label} pairs
 */
export const getAllSupportedAccountTypes = (
  withExtendedTypes: boolean = false,
  disableJointAccounts: boolean = false,
) => {
  const jointAccountTypes = disableJointAccounts ? [] : jointAccountTypeLabels;
  const accountTypes = withExtendedTypes
    ? [
        ...individualTypeOptions,
        ...jointAccountTypes,
        ...commonEntityAccountTypeLabels,
        ...extendedEntityAccountTypeLabels,
      ]
    : [...individualTypeOptions, ...jointAccountTypes, ...commonEntityAccountTypeLabels];

  return accountTypes.map((option) => ({
    value: option.key,
    label: option.label,
  }));
};
